import React, { useCallback, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  cancelMultibrandPolicy,
  cancelRenewalModalVisibleChanged,
  getAllBrandsData,
  requestCancelRenewal,
} from '../../../../actions';

import { useDirectCancel, useMixpanel } from '../../../../hooks';

import { EV54 } from '../../../../services/mixpanel';
import { getNowISOString } from '../../../../services/utils';

import { ModalContainer } from '../../../common';
import LoadingButton from '../../../common/LoadingButton';

import '../css/CancelRenewalModal.css';

const CancelRenewalModal = ({ petInfo = {} }) => {
  const { policyNumber } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation('changeCoverage');
  const showDirectCancel = useDirectCancel();
  const { sendEvent } = useMixpanel();
  const store = useSelector(({
    billingPayments,
    personalInformation,
    policies,
  }) => ({
    ...policies.changeCoverage,
    billingPayments,
    personalInformation,
  }));
  const {
    billingPayments: { allBrandsData },
    cancelMultibrandPolicy: {
      loading,
      success,
    },
    cancelRenewalLoading,
    cancelRenewalModalVisible,
    cancelRenewalRequestSent,
    personalInformation: { userInfo },
    rate: { effectiveDate },
  } = store;

  useEffect(() => {
    dispatch(getAllBrandsData());
  }, []);

  const handleKeepClick = useCallback(() => {
    dispatch(cancelRenewalModalVisibleChanged(false));
  }, []);

  const handleCancelClick = useCallback(() => {
    let policyId = null;
    if (Array.isArray(allBrandsData)) {
      const foundPolicy = allBrandsData.find(
        (policy) => String(policy.PolicyNumber) === String(policyNumber),
      );
      policyId = foundPolicy?.PolicyId;
    }

    const {
      Company,
      CompanyDesign,
      FirstName,
      LastName,
    } = userInfo;
    const customerName = `${FirstName} ${LastName}`;

    const dateISOString = moment(effectiveDate).toISOString();
    const nowISOString = getNowISOString();

    if (showDirectCancel) {
      dispatch(cancelMultibrandPolicy({ policyId }));
    } else {
      dispatch(requestCancelRenewal({
        customerName,
        effectiveDate,
        effectiveDateUtc: dateISOString,
        marketingChannel: Company?.OriginId,
        nowTimestampUtc: nowISOString,
        petName: petInfo?.petName || '',
        policyNumber,
        systemName: CompanyDesign?.Name,
      }));
    }

    sendEvent(EV54({
      date: nowISOString,
      policyBrand: Company?.OriginId,
      policyNumber,
    }));
  }, [
    effectiveDate,
    petInfo,
    policyNumber,
    userInfo,
    showDirectCancel,
    allBrandsData,
  ]);

  return (
    <ModalContainer
      cancelable
      handleOutClick={handleKeepClick}
      show={cancelRenewalModalVisible}
    >
      <div className="Cancel-renewal-modal-container">
        <h3>{t('options.cancelRenewal')}</h3>

        <p>{t('options.cancelRenewalDescription')}</p>

        <p>{t('options.cancelRenewalNote')}</p>

        <div className="Cancel-renewal-modal-buttons-container">
          <LoadingButton
            className={showDirectCancel
              ? 'Cancel-renewal-direct-modal-btn'
              : 'Cancel-renewal-modal-btn'}
            loading={showDirectCancel
              ? loading
              : cancelRenewalLoading}
            onClick={handleCancelClick}
            success={showDirectCancel
              ? success
              : cancelRenewalRequestSent}
            text={t('options.cancelRenewal')}
          />

          <button
            className="Cancel-renewal-modal-keep-btn"
            disabled={showDirectCancel
              ? loading
              : cancelRenewalLoading}
            onClick={handleKeepClick}
            type="button"
          >
            {t('options.goBack')}
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};

export { CancelRenewalModal };
